import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaClipboardList, 
  FaCheck, 
  FaTimes, 
  FaSpinner,
  FaCalendarAlt,
  FaUser,
  FaInbox,
  FaClock,
  FaBan
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './LeaveApplications.css';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';

const LeaveApplications = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [actionType, setActionType] = useState('');
  const [processing, setProcessing] = useState(false);
  const baseUrl = "https://api.burrayhr.com";
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  const fetchLeaveRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/leaves/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('All leaves:', response.data);
      
      // Filter leaves based on user role
      let filteredLeaves = [];
      if (userRole === 'md') {
        // MD can see HR manager leaves
        filteredLeaves = response.data.filter(leave => 
          leave.role === 'hr_manager'
        );
      } else if (userRole === 'hr_manager') {
        // HR manager can see staff leaves
        filteredLeaves = response.data.filter(leave => 
          leave.role === 'staff'
        );
      }
      
      console.log('Filtered leaves:', filteredLeaves);
      setLeaveRequests(filteredLeaves);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      showAlert('error', 'Failed to fetch leave requests');
      setLeaveRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    Swal.fire({
      icon: type,
      title: type === 'success' ? 'Success!' : 'Error!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      background: type === 'success' ? '#d4edda' : '#f8d7da',
      color: type === 'success' ? '#155724' : '#721c24',
    });
  };

  const handleAction = async (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    
    const { value: message } = await Swal.fire({
      title: `${action === 'approved' ? 'Approve' : 'Reject'} Leave Request`,
      html: `
        <div class="leave-response-modal" style="
          width: 100%;
          overflow-x: hidden;
          padding: 0 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
        ">
          <div style="
            background-color: #f8fafc;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            border: 1px solid #e2e8f0;
            width: 100%;
            max-width: 400px;
            text-align: center;
          ">
            <p style="
              margin-bottom: 15px;
              color: #1e293b;
              font-size: 0.95rem;
              font-weight: 500;
              text-align: center;
            ">${action === 'approved' ? 'Add an optional approval message' : 'Add a reason for rejection'}</p>
            <textarea 
              id="responseMessage" 
              class="swal2-textarea" 
              placeholder="${action === 'approved' ? 'Enter an optional approval message...' : 'Enter reason for rejection...'}"
              style="
                width: 100%;
                min-height: 120px;
                margin-bottom: 15px;
                padding: 12px;
                border: 1px solid #e2e8f0;
                border-radius: 6px;
                font-size: 0.95rem;
                line-height: 1.5;
                resize: vertical;
                background-color: white;
                transition: border-color 0.2s ease;
                font-family: inherit;
                display: block;
                margin-left: auto;
                margin-right: auto;
              "
            ></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: action === 'approved' ? 'Approve' : 'Reject',
      confirmButtonColor: action === 'approved' ? '#28a745' : '#dc3545',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (action === 'rejected' && !value) {
          return 'Please provide a reason for rejection';
        }
        return null;
      },
      preConfirm: () => {
        const messageInput = document.getElementById('responseMessage');
        return messageInput ? messageInput.value : '';
      }
    });

    console.log('Action:', action);
    console.log('Message from modal:', message);

    if (message !== undefined) {
      await handleSubmitResponse(request.id, action, message);
    }
  };

  const handleSubmitResponse = async (requestId, action, message) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const requestData = {
        status: action,
        response_message: message || null
      };

      console.log('Sending request data:', requestData);

      const response = await axios.patch(
        `${baseUrl}/api/leaves/${requestId}/`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API Response:', response.data);

      if (response.data) {
        showAlert('success', `Leave request ${action} successfully`);
        // Refresh the list to show updated data
        await fetchLeaveRequests();
      }
    } catch (error) {
      console.error('Error updating leave request:', error);
      console.error('Error details:', error.response?.data);
      showAlert('error', error.response?.data?.message || `Failed to ${action} leave request`);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays === 1 ? '1 day' : `${diffDays} days`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  if (loading) {
    return (
      <>
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="leave-applications">
            <div className="leave-header">
              <FaSpinner className="header-icon fa-spin" />
              <h2>Loading leave requests...</h2>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="main-content">
        <div className="leave-applications">
          <div className="leave-header">
            <div className="header-content">
              <div className="header-icon-container">
                <FaClipboardList style={{color: 'navy'}} className="" />
              </div>
              <div className="header-text">
                <h4 className="header-title text-dark">
                  {userRole === 'md' ? 'Immediate Manager Leave Applications' : 'Workers Leave Applications'}
                </h4>
                <p className="header-subtitle">
                  {userRole === 'md' 
                    ? 'Review and manage leave requests from immediate managers'
                    : 'Review and manage leave requests from workers'}
                </p>
              </div>
            </div>
          </div>

          {leaveRequests.length === 0 ? (
            <div className="empty-state">
              <FaInbox className="empty-state-icon" />
              <h3 className="empty-state-text">
                {userRole === 'md' ? 'No HR Manager Leave Requests' : 'No Staff Leave Requests'}
              </h3>
              <p className="empty-state-subtext">
                {userRole === 'md' 
                  ? 'There are no HR manager leave requests to display at this time.'
                  : 'There are no staff leave requests to display at this time.'}
              </p>
            </div>
          ) : (
            <div className="leave-cards-container">
              {leaveRequests.map(request => (
                <div key={request.id} className="leave-card">
                  <div className="card-header">
                    <div className="employee-info">
                      <div className="employee-avatar">
                        <FaUser />
                      </div>
                      <div className="employee-details">
                        <h3 className="employee-name">{request.employee_name}</h3>
                        <p className="leave-type">{request.leave_type} Leave</p>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="leave-info">
                      <div className="info-item">
                        <span className="info-label">Start Date</span>
                        <span className="info-value">
                          <FaCalendarAlt /> {formatDate(request.start_date)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">End Date</span>
                        <span className="info-value">
                          <FaCalendarAlt /> {formatDate(request.end_date)}
                        </span>
                      </div>
                    </div>

                    <div className="duration-badge">
                      Duration: <FaClock /> {request.duration} {request.leave_type.toLowerCase() === 'maternity' || request.leave_type.toLowerCase() === 'sick' ? 'days' : 'working days'}
                    </div>

                    <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                      {request.status === 'approved' ? <FaCheck /> : 
                       request.status === 'rejected' ? <FaTimes /> : 
                       request.status === 'cancelled' ? <FaBan /> :
                       <FaSpinner className="fa-spin" />}
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>

                    {request.reason && (
                      <div className="reason-text">
                        <strong>Reason for Leave:</strong>
                        <p>{request.reason}</p>
                      </div>
                    )}

                    {(request.status === 'approved' || request.status === 'rejected') && request.response_message && (
                      <div className={`response-message ${request.status === 'approved' ? 'approved-message' : 'rejected-message'}`}>
                        <strong>{request.status === 'approved' ? 'Approval Message:' : 'Rejection Reason:'}</strong>
                        <p>{request.response_message}</p>
                      </div>
                    )}

                    {request.status === 'pending' && (
                      <div className="card-footer">
                        <button
                          className="btn btn-approve"
                          onClick={() => handleAction(request, 'approved')}
                        >
                          <FaCheck /> Approve
                        </button>
                        <button
                          className="btn btn-reject"
                          onClick={() => handleAction(request, 'rejected')}
                        >
                          <FaTimes /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LeaveApplications;
